import { useMemo } from 'react';
import { useGetUserPreferencesQuery } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { useUserPreferencesQueryArg } from './useTenantIdForPreferences';
import {
  canSeeScriptLibrary,
  DEFAULT_SCRIPT_LIBRARY_ACCESS,
  normalizeScriptLibraryAccessConfig,
  type ScriptLibraryAccessConfig,
} from '../utils/scriptLibraryAccess';

/**
 * Resolved Script Library visibility for the current user in the active tenant.
 *
 * `canSee` is the gate every render-site should consult (EA page, Unibox
 * compose, etc.). Admins always return true regardless of config so they can
 * configure the policy and recover from mis-configurations. The hook returns
 * `loaded=false` while preferences are still in flight; render-sites should
 * generally show the panel optimistically when not loaded (default policy is
 * "open to all" anyway).
 */
export interface ScriptLibraryAccessResult {
  canSee: boolean;
  isAdmin: boolean;
  config: ScriptLibraryAccessConfig;
  loaded: boolean;
}

export function useScriptLibraryAccess(): ScriptLibraryAccessResult {
  const arg = useUserPreferencesQueryArg();
  const { data: prefsData, isSuccess, isLoading } = useGetUserPreferencesQuery(arg, {
    skip: !arg,
  });
  const { currentUser } = useUser() as {
    currentUser: { role?: string; department?: string } | null;
  };

  const config = useMemo<ScriptLibraryAccessConfig>(() => {
    const raw =
      (prefsData as any)?.preferences?.scriptLibraryAccess ??
      (prefsData as any)?.scriptLibraryAccess;
    if (raw === undefined) return DEFAULT_SCRIPT_LIBRARY_ACCESS;
    return normalizeScriptLibraryAccessConfig(raw);
  }, [prefsData]);

  const isAdmin = String(currentUser?.role || '').trim().toLowerCase() === 'admin';
  const canSee = canSeeScriptLibrary(
    { role: currentUser?.role, department: currentUser?.department },
    config
  );

  return {
    canSee,
    isAdmin,
    config,
    loaded: isSuccess || !isLoading,
  };
}
